/**
 * Offline submission queue for polling agents.
 * Stores results in localStorage when the network is down, then auto-syncs.
 * If a queue item references a photo (`_sheet_key`), the photo blob lives in
 * IndexedDB (see lib/photoStore.js) and is uploaded on sync before the result
 * itself is submitted.
 */
import { getPhoto, deletePhoto } from "@/lib/photoStore";

const KEY = "bw_offline_queue_v1";
const EVT = "bw:queue-changed";

export function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function writeQueue(items) {
  localStorage.setItem(KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent(EVT));
}

export function enqueueResult(payload, meta = {}) {
  const q = readQueue();
  const item = {
    temp_id:
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    payload,
    meta,
    rehearsal: typeof localStorage !== "undefined" && localStorage.getItem("cw_sim_offline") === "1",
    queued_at: new Date().toISOString(),
    retry_count: 0,
    last_error: null,
  };
  q.push(item);
  writeQueue(q);
  return item;
}

export function removeFromQueue(temp_id) {
  const item = readQueue().find((x) => x.temp_id === temp_id);
  writeQueue(readQueue().filter((x) => x.temp_id !== temp_id));
  if (item?.payload?._sheet_key) deletePhoto(item.payload._sheet_key).catch(() => {});
}

function updateRetry(temp_id, error) {
  writeQueue(
    readQueue().map((x) =>
      x.temp_id === temp_id
        ? { ...x, retry_count: (x.retry_count || 0) + 1, last_error: error }
        : x
    )
  );
}

async function uploadStoredPhoto(api, sheetKey) {
  const rec = await getPhoto(sheetKey);
  if (!rec) return null;
  const fd = new FormData();
  const file = new File([rec.blob], rec.name || `sheet-${sheetKey}.jpg`, { type: rec.type || "image/jpeg" });
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

export async function flushQueue(api) {
  const items = readQueue();
  const out = { synced: 0, failed: 0, duplicate: 0, rehearsed: 0 };
  for (const item of items) {
    try {
      let payload = { ...item.payload };
      const sheetKey = payload._sheet_key;
      if (item.rehearsal) {
        // Rehearsal submissions never become real results — just record completion.
        await api.post("/rehearsal/complete", { polling_unit_id: payload.polling_unit_id, election_id: payload.election_id, vote_sum: item.meta?.vote_sum ?? null, had_photo: !!sheetKey });
        if (sheetKey) await deletePhoto(sheetKey).catch(() => {});
        removeFromQueue(item.temp_id);
        out.rehearsed++;
        continue;
      }
      if (sheetKey && !payload.sheet_path) {
        try {
          const path = await uploadStoredPhoto(api, sheetKey);
          if (path) payload.sheet_path = path;
        } catch (e) {
          // if photo upload fails, keep item in queue for retry
          updateRetry(item.temp_id, `photo upload failed: ${e?.message || e}`);
          out.failed++;
          continue;
        }
      }
      delete payload._sheet_key;
      await api.post("/results", payload);
      // success — clean up photo + queue entry
      if (sheetKey) await deletePhoto(sheetKey).catch(() => {});
      removeFromQueue(item.temp_id);
      out.synced++;
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e.message || "sync failed";
      if (
        status === 400 &&
        typeof msg === "string" &&
        msg.toLowerCase().includes("already submitted")
      ) {
        removeFromQueue(item.temp_id);
        out.duplicate++;
      } else {
        updateRetry(item.temp_id, msg);
        out.failed++;
      }
    }
  }
  return out;
}

export function subscribeQueue(cb) {
  const handler = () => cb(readQueue());
  window.addEventListener(EVT, handler);
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) handler();
  });
  return () => window.removeEventListener(EVT, handler);
}

export function isNetworkError(err) {
  if (!err) return false;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (typeof localStorage !== "undefined" && localStorage.getItem("cw_sim_offline") === "1") return true;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  const msg = (err.message || "").toLowerCase();
  return (
    !err.response &&
    (msg.includes("network") ||
      msg.includes("failed to fetch") ||
      msg.includes("timeout"))
  );
}
